import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
  static targets = ["input", "submit", "error", "form", "status"];
  declare readonly inputTarget: HTMLDivElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasStatusTarget: boolean;
  private busy = false;
  private sending = false;
  private fileCount = 0;

  connect(): void {
    this.changed();
  }

  changed(): void {
    const text = this.inputTarget.textContent || "";
    this.submitTarget.disabled = this.busy || this.sending || text.length > 12_000 || (text.trim().length === 0 && this.fileCount === 0);
  }

  filesChanged(event: CustomEvent<{ count: number }>): void {
    this.fileCount = event.detail.count;
    this.errorTarget.hidden = true;
    this.changed();
  }

  uploadRejected(event: CustomEvent<{ message: string }>): void {
    event.preventDefault();
    this.errorTarget.textContent = event.detail.message;
    this.errorTarget.hidden = false;
  }

  keydown(event: KeyboardEvent): void {
    if (!this.inputTarget.contains(event.target as Node)) return;
    if (event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!this.submitTarget.disabled) this.formTarget.requestSubmit();
    }
  }

  paste(event: ClipboardEvent): void {
    if (event.defaultPrevented || !this.inputTarget.contains(event.target as Node)) return;
    const data = event.clipboardData;
    if (!data) return;
    // Some sources offer only HTML; read its text rather than paste nothing.
    const text = data.getData("text/plain") || new DOMParser().parseFromString(data.getData("text/html"), "text/html").body.textContent || "";
    if (!text) return;

    event.preventDefault();
    // Native insertion preserves the caret, selection replacement, and undo history.
    document.execCommand("insertText", false, text);
  }

  suggest(event: Event): void {
    this.setContent((event.currentTarget as HTMLElement).dataset.prompt || "");
    this.changed();
    this.inputTarget.focus();
  }

  private setContent(value: string): void {
    this.inputTarget.textContent = value;
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  started(): void {
    this.sending = true;
    this.errorTarget.textContent = this.errorTarget.dataset.defaultMessage || "";
    this.errorTarget.hidden = true;
    this.submitTarget.disabled = true;
    this.element.setAttribute("aria-busy", "true");
  }

  finished(event: CustomEvent<{ success: boolean }>): void {
    this.sending = false;
    this.element.removeAttribute("aria-busy");
    if (event.detail.success) {
      this.setContent("");
      this.formTarget.querySelectorAll<HTMLElement>('[data-controller~="dropzone"]').forEach((element) => {
        element.dispatchEvent(new CustomEvent("assistant-composer:clear-uploads"));
      });
      this.fileCount = 0;
    } else this.errorTarget.hidden = false;
    this.changed();
  }

  beforeStreamRender(event: CustomEvent<{ render: (stream: HTMLElement) => void | Promise<void> }>): void {
    if (!this.hasStatusTarget) return;
    const stream = event.target as HTMLElement;
    if (stream.getAttribute("target") !== this.statusTarget.id) return;
    const incoming = stream.querySelector<HTMLTemplateElement>("template")?.content
      .querySelector<HTMLElement>('[data-assistant-composer-target~="status"]');
    if (!incoming) return;

    // data-status-version is server-owned (Assistant::TurnState#version); only compare it here.
    const render = event.detail.render;
    event.detail.render = (element: HTMLElement) => {
      if (!this.hasStatusTarget) return;
      const incomingVersion = Number(incoming.dataset.statusVersion || 0);
      const currentVersion = Number(this.statusTarget.dataset.statusVersion || 0);
      if (incomingVersion < currentVersion) return;
      return render(element);
    };
  }

  statusTargetConnected(status: HTMLElement): void {
    this.busy = ["queued", "working"].includes(status.dataset.state || "");
    this.changed();
  }
}
